import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useBuilding } from "../../context/BuildingContext";
import { journalApi, categoriesApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import EmptyState from "../../components/common/EmptyState";
import SearchInput from "../../components/common/SearchInput";
import PageHeader from "../../components/common/PageHeader";
import DateRangePicker from "../../components/common/DateRangePicker";
import { formatCurrency, formatDate } from "../../utils/formatters";
import { JournalEntry, JournalFilters } from "../../modules/journalTypes";

const JournalEntries: React.FC = () => {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allCategories, setAllCategories] = useState<
    { nominalCode: string; name: string; parentCode?: string }[]
  >([]);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(
    null,
  );
  const [savingCategoryId, setSavingCategoryId] = useState<string | null>(null);
  const [filters, setFilters] = useState<JournalFilters>({
    search: "",
    category: "",
    type: "",
    status: "",
    startDate: null,
    endDate: null,
  });

  const { currentBuilding } = useBuilding();

  // Category options for filtering
  const categoryOptions = [
    { value: "", label: "All Categories" },
    { value: "maintenance", label: "Maintenance" },
    { value: "utilities", label: "Utilities" },
    { value: "insurance", label: "Insurance" },
    { value: "management", label: "Management" },
    { value: "reserve", label: "Reserve Fund" },
    { value: "serviceCharge", label: "Service Charge" },
    { value: "other", label: "Other" },
  ];

  // Type options for filtering
  const typeOptions = [
    { value: "", label: "All Types" },
    { value: "income", label: "Income" },
    { value: "bill", label: "Expense" },
  ];

  // Status options for filtering
  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "draft", label: "Draft" },
    { value: "posted", label: "Posted" },
    { value: "reversed", label: "Reversed" },
  ];

  useEffect(() => {
    const fetchJournalEntries = async () => {
      if (!currentBuilding?.id) return;

      try {
        setLoading(true);
        const [raw, cats] = await Promise.all([
          journalApi.getEntries(currentBuilding.id, {}),
          categoriesApi.getAll(),
        ]);
        const data: JournalEntry[] = Array.isArray(raw) ? raw : [];
        setEntries(data);
        setFilteredEntries(data);
        setAllCategories(Array.isArray(cats) ? cats : []);
      } catch (err) {
        console.error("Error fetching journal entries:", err);
        setError("Failed to load journal entries. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchJournalEntries();
  }, [currentBuilding?.id]);

  useEffect(() => {
    if (entries.length > 0) {
      let result = [...entries];

      // Apply search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        result = result.filter(
          (entry) =>
            entry.description.toLowerCase().includes(searchTerm) ||
            entry.reference?.toLowerCase().includes(searchTerm) ||
            entry.category.toLowerCase().includes(searchTerm),
        );
      }

      // Apply category filter
      if (filters.category) {
        result = result.filter((entry) => entry.category === filters.category);
      }

      // Apply type filter
      if (filters.type) {
        result = result.filter((entry) => entry.type === filters.type);
      }

      // Apply status filter
      if (filters.status) {
        result = result.filter(
          (entry) => (entry.status || "posted") === filters.status,
        );
      }

      // Apply date range filter
      if (filters.startDate) {
        result = result.filter((entry) => entry.date >= filters.startDate!);
      }

      if (filters.endDate) {
        result = result.filter((entry) => entry.date <= filters.endDate!);
      }

      setFilteredEntries(result);
    }
  }, [entries, filters]);

  const handleFilterChange = (name: string, value: string | null) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateRangeChange = (
    startDate: string | null,
    endDate: string | null,
  ) => {
    setFilters((prev) => ({
      ...prev,
      startDate,
      endDate,
    }));
  };

  const handleCategoryChange = async (
    entry: JournalEntry,
    nominalCode: string,
  ) => {
    const cat = allCategories.find((c) => c.nominalCode === nominalCode);
    if (!cat) return;
    setSavingCategoryId(entry.id);
    setEditingCategoryId(null);
    try {
      await journalApi.updateEntry(entry.id, {
        buildingId: currentBuilding?.id,
        category: cat.name,
        nominalCode: cat.nominalCode,
      });
      const update = (list: JournalEntry[]) =>
        list.map((e) =>
          e.id === entry.id
            ? { ...e, category: cat.name, nominalCode: cat.nominalCode }
            : e,
        );
      setEntries((prev) => update(prev));
      setFilteredEntries((prev) => update(prev));
    } catch (err) {
      console.error("Failed to update category:", err);
    } finally {
      setSavingCategoryId(null);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      category: "",
      type: "",
      status: "",
      startDate: null,
      endDate: null,
    });
  };

  // Calculate summary data for the ledger
  const calculateSummary = () => {
    const income = filteredEntries
      .filter((entry) => entry.type === "income")
      .reduce((sum, entry) => sum + entry.amount, 0);

    const expenses = filteredEntries
      .filter((entry) => entry.type === "bill")
      .reduce((sum, entry) => sum + entry.amount, 0);

    const balance = income - expenses;

    // amounts are stored in pence; divide by 100 for display
    return {
      income: income / 100,
      expenses: expenses / 100,
      balance: balance / 100,
    };
  };

  const summary = calculateSummary();

  const handleExportCSV = () => {
    const headers = [
      "Date",
      "Description",
      "Reference",
      "Category",
      "Type",
      "Status",
      "Debit",
      "Credit",
    ];
    const rows = filteredEntries.map((entry) => [
      entry.date,
      `"${(entry.description || "").replace(/"/g, '""')}"`,
      entry.reference || "",
      entry.category || "",
      entry.type || "",
      entry.status || "posted",
      entry.type === "income" ? (entry.amount / 100).toFixed(2) : "",
      entry.type === "bill" ? (entry.amount / 100).toFixed(2) : "",
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `journal-entries-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <Spinner message="Loading journal entries..." />;
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  return (
    <div className="">
      <PageHeader
        title="Journal"
        description={`Financial transactions for ${
          currentBuilding?.name || "your building"
        }`}
        actions={
          <div className="flex gap-2">
            <button
              onClick={handleExportCSV}
              disabled={filteredEntries.length === 0}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              Export CSV
            </button>
            <Link
              to="/dashboard/journal/new-entry"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              New Entry
            </Link>
          </div>
        }
      />

      {/* Filters */}
      <div className="mt-6 bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
        <div className="md:grid md:grid-cols-4 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Filters
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Narrow down journal entries by category, type, or date range.
            </p>
          </div>
          <div className="mt-5 md:mt-0 md:col-span-3">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div className="sm:col-span-6">
                <SearchInput
                  placeholder="Search journal entries..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  onClear={() => handleFilterChange("search", "")}
                />
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="category"
                  className="block text-sm font-medium text-gray-700"
                >
                  Category
                </label>
                <select
                  id="category"
                  name="category"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  value={filters.category}
                  onChange={(e) =>
                    handleFilterChange("category", e.target.value)
                  }
                >
                  {categoryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="type"
                  className="block text-sm font-medium text-gray-700"
                >
                  Type
                </label>
                <select
                  id="type"
                  name="type"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  value={filters.type}
                  onChange={(e) => handleFilterChange("type", e.target.value)}
                >
                  {typeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="status"
                  className="block text-sm font-medium text-gray-700"
                >
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-6">
                <DateRangePicker
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                  onDateRangeChange={handleDateRangeChange}
                  startDateLabel="From Date"
                  endDateLabel="To Date"
                />
              </div>

              <div className="sm:col-span-6 flex justify-end">
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Summary
          </h3>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Income
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-green-600">
                  {formatCurrency(summary.income)}
                </dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Expenses
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-red-600">
                  {formatCurrency(summary.expenses)}
                </dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Balance
                </dt>
                <dd
                  className={`mt-1 text-3xl font-semibold ${
                    summary.balance >= 0 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {formatCurrency(summary.balance)}
                </dd>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ledger Entries */}
      <div className="mt-8">
        {filteredEntries.length === 0 ? (
          <EmptyState
            title="No journal entries found"
            description={
              filters.search || filters.category || filters.type
                ? "No entries match your search criteria."
                : "You don't have any journal entries yet."
            }
            actionText={
              filters.search || filters.category || filters.type
                ? "Clear filters"
                : "Create your first entry"
            }
            actionHref={
              filters.search || filters.category || filters.type
                ? undefined
                : "/dashboard/journal/new-entry"
            }
            onAction={
              filters.search || filters.category || filters.type
                ? handleClearFilters
                : undefined
            }
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            }
          />
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Date
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Description
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Reference
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Code
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Category
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Debit
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Credit
                    </th>
                    <th scope="col" className="relative px-4 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {filteredEntries.map((entry) => {
                    const hasLines = (entry.lines?.length ?? 0) > 0;
                    return (
                      <React.Fragment key={entry.id}>
                        {/* Entry header row */}
                        <tr className="border-t border-gray-200 bg-gray-50">
                          <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatDate(entry.date)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            <div className="font-medium">
                              {entry.description}
                            </div>
                            {entry.comment &&
                              entry.comment !== entry.description && (
                                <div className="text-xs text-gray-400 mt-0.5">
                                  {entry.comment}
                                </div>
                              )}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                            {entry.reference || "-"}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap font-mono text-xs text-gray-400">
                            {entry.nominalCode || ""}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                            {savingCategoryId === entry.id ? (
                              <span className="text-xs text-gray-400 italic">
                                Saving…
                              </span>
                            ) : editingCategoryId === entry.id ? (
                              <select
                                autoFocus
                                className="text-xs border border-primary-400 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                defaultValue={entry.nominalCode ?? ""}
                                onChange={(e) =>
                                  handleCategoryChange(entry, e.target.value)
                                }
                                onBlur={() => setEditingCategoryId(null)}
                              >
                                <option value="" disabled>
                                  — pick category —
                                </option>
                                {allCategories
                                  .filter((c) => !c.parentCode)
                                  .map((parent) => (
                                    <optgroup
                                      key={parent.nominalCode}
                                      label={`${parent.nominalCode} ${parent.name}`}
                                    >
                                      {allCategories
                                        .filter(
                                          (c) =>
                                            c.parentCode === parent.nominalCode,
                                        )
                                        .map((child) => (
                                          <option
                                            key={child.nominalCode}
                                            value={child.nominalCode}
                                          >
                                            {child.nominalCode} – {child.name}
                                          </option>
                                        ))}
                                    </optgroup>
                                  ))}
                              </select>
                            ) : (
                              <button
                                onClick={() => setEditingCategoryId(entry.id)}
                                className="group flex items-center gap-1 cursor-pointer rounded hover:bg-gray-200 px-1 -mx-1"
                                title="Click to change category"
                              >
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-200 text-gray-700 group-hover:bg-primary-100 group-hover:text-primary-800">
                                  {entry.category
                                    ? entry.category.charAt(0).toUpperCase() +
                                      entry.category.slice(1)
                                    : "-"}
                                </span>
                              </button>
                            )}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm">
                            {(() => {
                              const status = entry.status || "posted";
                              const colors = {
                                draft: "bg-yellow-100 text-yellow-800",
                                posted: "bg-green-100 text-green-800",
                                reversed: "bg-red-100 text-red-800",
                              };
                              return (
                                <span
                                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    colors[status as keyof typeof colors] ||
                                    "bg-gray-100 text-gray-800"
                                  }`}
                                >
                                  {status.charAt(0).toUpperCase() +
                                    status.slice(1)}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-right text-sm text-green-600 font-medium">
                            {entry.type === "income"
                              ? formatCurrency(entry.amount / 100)
                              : ""}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-right text-sm text-red-600 font-medium">
                            {entry.type === "bill"
                              ? formatCurrency(entry.amount / 100)
                              : ""}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-right text-sm font-medium">
                            <Link
                              to={`/dashboard/journal/${entry.id}`}
                              className="text-primary-600 hover:text-blue-900"
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                        {/* Journal lines — one row each */}
                        {hasLines &&
                          entry.lines!.map((line) => (
                            <tr
                              key={line.id}
                              className="border-t border-gray-100 hover:bg-gray-50"
                            >
                              <td className="px-4 py-1.5 text-sm text-gray-300" />
                              <td className="px-4 py-1.5 text-sm text-gray-600 pl-8">
                                {line.description || "-"}
                              </td>
                              <td className="px-4 py-1.5 text-sm text-gray-400" />
                              <td className="px-4 py-1.5 font-mono text-xs text-gray-400">
                                {line.nominalCode}
                              </td>
                              <td className="px-4 py-1.5 text-sm text-gray-500">
                                {line.categoryName || "-"}
                              </td>
                              <td className="px-4 py-1.5" />
                              <td className="px-4 py-1.5 text-right font-mono text-sm text-gray-700">
                                {line.debit
                                  ? formatCurrency(line.debit / 100)
                                  : ""}
                              </td>
                              <td className="px-4 py-1.5 text-right font-mono text-sm text-gray-700">
                                {line.credit
                                  ? formatCurrency(line.credit / 100)
                                  : ""}
                              </td>
                              <td className="px-4 py-1.5" />
                            </tr>
                          ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                {/* Summary row at the bottom */}
                <tfoot className="bg-gray-50">
                  <tr className="border-t-2 border-gray-300">
                    <td
                      className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                      colSpan={6}
                    >
                      Total
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-green-600 font-medium">
                      {formatCurrency(summary.income)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-red-600 font-medium">
                      {formatCurrency(summary.expenses)}
                    </td>
                    <td></td>
                  </tr>
                  <tr>
                    <td
                      className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                      colSpan={6}
                    >
                      Balance
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                        summary.balance >= 0 ? "text-green-600" : "text-red-600"
                      }`}
                      colSpan={2}
                    >
                      {formatCurrency(summary.balance)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JournalEntries;
